/**
 * PolicyRuleParamEditor — Inline editor for check parameters
 *
 * Renders different inputs based on paramSchema type:
 * 'string' → text input, 'number' → number input, 'boolean' → toggle
 * Special handling for consent levels, registries, windows.
 */
import { Input, cn } from '@respectly/ui';
import { Switch } from '@respectly/ui';
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from '@respectly/ui';
import { Label } from '@respectly/ui';
import { PRE_DNC_REGISTRIES, PRE_CALLING_HOUR_WINDOWS } from '../../data/pre-rules';

// ============================================
// Known param enums
// ============================================

const CONSENT_LEVELS = [
  { value: 'NONE', label: 'None' },
  { value: 'OPTOUT', label: 'Opt-Out' },
  { value: 'IMPLICIT_OPT_IN', label: 'Implicit Opt-In (EBR)' },
  { value: 'EXPRESS_OPTIN', label: 'Express Opt-In' },
  { value: 'WRITTEN_EXPRESS_OPTIN', label: 'Written Express Opt-In' },
];

const REGISTRY_OPTIONS = PRE_DNC_REGISTRIES.map((r) => ({
  value: r.registryId,
  label: `${r.name} (${r.country})`,
}));

const WINDOW_OPTIONS = PRE_CALLING_HOUR_WINDOWS.map((w) => ({
  value: w.windowId,
  label: `${w.name}`,
}));

const CONSENT_PARAM_KEYS = new Set([
  'minConsent',
  'ifWirelessMinConsent',
]);

const REGISTRY_PARAM_KEYS = new Set(['registry']);
const WINDOW_PARAM_KEYS = new Set(['window']);

// ============================================
// Component
// ============================================

interface PolicyRuleParamEditorProps {
  checkId: string;
  paramSchema: Record<string, string> | null;
  params: Record<string, unknown> | null;
  onChange: (params: Record<string, unknown>) => void;
  className?: string;
}

export function PolicyRuleParamEditor({
  paramSchema,
  params,
  onChange,
  className,
}: PolicyRuleParamEditorProps) {
  if (!paramSchema) return null;

  const currentParams = params ?? {};

  const handleChange = (key: string, value: unknown) => {
    onChange({ ...currentParams, [key]: value });
  };

  const entries = Object.entries(paramSchema);

  return (
    <div className={cn('grid grid-cols-2 gap-x-3 gap-y-2 items-center', className)}>
      {entries.map(([key, type]) => (
        <ParamField
          key={key}
          paramKey={key}
          type={type}
          value={currentParams[key]}
          onChange={(v) => handleChange(key, v)}
        />
      ))}
    </div>
  );
}

// ============================================
// ParamField
// ============================================

interface ParamFieldProps {
  paramKey: string;
  type: string;
  value: unknown;
  onChange: (value: unknown) => void;
}

function ParamField({ paramKey, type, value, onChange }: ParamFieldProps) {
  const label = formatParamLabel(paramKey);

  // Special select for consent level params
  if (CONSENT_PARAM_KEYS.has(paramKey)) {
    return (
      <>
        <Label className="text-xs text-slate-600 dark:text-slate-400 text-right">
          {label}
        </Label>
        <Select
          value={String(value ?? '')}
          onValueChange={(v) => onChange(v)}
        >
          <SelectTrigger className="h-7 text-xs">
            <SelectValue placeholder="Select level..." />
          </SelectTrigger>
          <SelectContent>
            {CONSENT_LEVELS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </>
    );
  }

  // Special select for registry params
  if (REGISTRY_PARAM_KEYS.has(paramKey)) {
    return (
      <>
        <Label className="text-xs text-slate-600 dark:text-slate-400 text-right">
          {label}
        </Label>
        <Select
          value={String(value ?? '')}
          onValueChange={(v) => onChange(v)}
        >
          <SelectTrigger className="h-7 text-xs">
            <SelectValue placeholder="Select registry..." />
          </SelectTrigger>
          <SelectContent>
            {REGISTRY_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </>
    );
  }

  // Special select for window params
  if (WINDOW_PARAM_KEYS.has(paramKey)) {
    return (
      <>
        <Label className="text-xs text-slate-600 dark:text-slate-400 text-right">
          {label}
        </Label>
        <Select
          value={String(value ?? '')}
          onValueChange={(v) => onChange(v)}
        >
          <SelectTrigger className="h-7 text-xs">
            <SelectValue placeholder="Select window..." />
          </SelectTrigger>
          <SelectContent>
            {WINDOW_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </>
    );
  }

  // Boolean → Switch
  if (type === 'boolean') {
    return (
      <>
        <Label className="text-xs text-slate-600 dark:text-slate-400 text-right">
          {label}
        </Label>
        <div>
          <Switch
            checked={Boolean(value)}
            onCheckedChange={(checked: boolean) => onChange(checked)}
            className="scale-75"
          />
        </div>
      </>
    );
  }

  // Number → number input
  if (type === 'number') {
    return (
      <>
        <Label className="text-xs text-slate-600 dark:text-slate-400 text-right">
          {label}
        </Label>
        <Input
          type="number"
          value={value !== null && value !== undefined ? String(value) : ''}
          onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
          className="h-7 text-xs"
        />
      </>
    );
  }

  // Default string → text input
  return (
    <>
      <Label className="text-xs text-slate-600 dark:text-slate-400 text-right">
        {label}
      </Label>
      <Input
        type="text"
        value={String(value ?? '')}
        onChange={(e) => onChange(e.target.value || null)}
        className="h-7 text-xs"
      />
    </>
  );
}

// ============================================
// Helpers
// ============================================

function formatParamLabel(key: string): string {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (s) => s.toUpperCase())
    .trim();
}
